import { get, remove, put } from '../lib'

interface ResponseType {
  id: string
  analyzer: {
    id: string
    name: string
  }
  datasets: Array<{
    id: string
    name: string
  }>
  job_id: string
  script: {
    id: string
    name: string
  }
  configs: Array<{
    id: string
    name: string
  }>
  file: Array<{
    id: string
    size: number
    extension: string
    filename: string
    created_at: string
  }>
  status: string
  created_at: string
  updated_at: string
  recycled: string | null
  retention_lock: boolean
}

interface ResponseFile {
  file: string
  filename: string
  ipa_result_id: string
}

export const IPA_RESULT_ACTIONS = {
  LOCK: 'lock',
  UNLOCK: 'unlock',
  RECYCLE: 'recycle',
  RESTORE: 'restore'
} as const

export type IPAResultAction =
  typeof IPA_RESULT_ACTIONS[keyof typeof IPA_RESULT_ACTIONS]

const HEADER_MAX = 'x-wivi-max'
const HEADER_OFFSET = 'x-wivi-offset'
const HEADER_TOTAL = 'x-wivi-total'

export interface ResultsPagination {
  max: number
  offset: number
  total: number
}

export interface ResultsResponse {
  data: ResponseType[]
  pagination: ResultsPagination
}

const parseHeaderNumber = (
  headers: Headers,
  name: string,
  fallback = 0
): number => {
  const value = headers.get(name)
  const parsed = value !== null ? Number(value) : NaN
  return Number.isFinite(parsed) ? parsed : fallback
}

export const getAllResults = async (
  query: {
    [k: string]: string | number
  } = {}
): Promise<ResultsResponse> => {
  const response = await get<{ data: ResponseType[]; headers: Headers }>({
    path: '/ipa/result',
    query,
    includeHeaders: true
  })

  return {
    data: response.data,
    pagination: {
      max: parseHeaderNumber(response.headers, HEADER_MAX, 100),
      offset: parseHeaderNumber(response.headers, HEADER_OFFSET),
      total: parseHeaderNumber(response.headers, HEADER_TOTAL)
    }
  }
}

export const getResult = async (
  result_id: string,
  query: {
    [k: string]: string | number
  } = {}
): Promise<ResponseType> => {
  return get<ResponseType>({
    path: `/ipa/result/${result_id}`,
    query
  })
}

export const getResultFileData = async (
  resultFileId: string,
  query: {
    [k: string]: string
  } = {}
): Promise<ResponseFile> => {
  return get<ResponseFile>({
    path: `/ipa/result/file/${resultFileId}`,
    query
  })
}

export const removeResult = async (id: string): Promise<null> => {
  return remove({
    path: '/ipa/result',
    id
  })
}

export const updateResultAction = async (
  resultId: string,
  action: IPAResultAction
): Promise<ResponseType> => {
  return put({
    path: `/ipa/result/${resultId}/${action}`,
    body: {}
  })
}
